import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Badge,
    Button,
    Col,
    Collapse,
    Dropdown,
    Form,
    InputGroup,
    Row,
    Stack,
} from 'react-bootstrap';
import {
    FaSearch,
    FaFilter,
    FaSlidersH,
    FaSortUp,
    FaSortDown,
    FaSort,
    FaTrash,
    FaChevronDown,
    FaChevronUp,
    FaTimes
} from 'react-icons/fa';

import DatePicker from 'react-datepicker';

import '../../assets/styles/SmartTopTool.css';
import 'react-datepicker/dist/react-datepicker.css';


const AdvancedSearchTool = ({
    globalSearch = '',
    setGlobalSearch = () => { },
    globalSearchPlaceholder = 'Search...',
    exactSearch = {},
    setExactSearch = () => { },
    exactSearchFields = [],
    filterOptions = {},
    filters = {},
    setFilters = () => { },
    debounceMs = 400,
}) => {
    const [inputValue, setInputValue] = useState(globalSearch || '');
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [exactDraft, setExactDraft] = useState(exactSearch || {});

    useEffect(() => {
        setInputValue(globalSearch || '');
    }, [globalSearch]);

    useEffect(() => {
        setExactDraft(exactSearch || {});
    }, [exactSearch]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const next = inputValue.trim();

            if (next !== (globalSearch || '')) {
                setGlobalSearch(next);
            }
        }, debounceMs);

        return () => window.clearTimeout(timer);
    }, [
        inputValue,
        globalSearch,
        setGlobalSearch,
        debounceMs,
    ]);

    const applyAdvancedSearch = () => {
        const cleaned = Object.fromEntries(
            Object.entries(exactDraft).map(([key, value]) => [
                key,
                String(value || '').trim(),
            ]),
        );

        setExactSearch(cleaned);
    };

    const clearAdvancedSearch = () => {
        const empty = Object.fromEntries(
            exactSearchFields.map((field) => [field.key, '']),
        );

        setExactDraft(empty);
        setExactSearch(empty);
    };

    const clearGlobalSearch = () => {
        setInputValue('');
        setGlobalSearch('');
    };

    const filterCount = Object.values(filters || {}).reduce(
        (total, value) => {
            if (!Array.isArray(value)) return total;
            return total + value.length;
        },
        0,
    );

    const advancedSearchCount = Object.values(exactSearch || {}).filter(
        (value) => String(value || '').trim(),
    ).length;

    const hasFilters = filterCount > 0;
    const hasAdvancedSearch = advancedSearchCount > 0;

    const hasAdvancedDraft = Object.values(exactDraft || {}).some(
        (value) => String(value || '').trim(),
    );

    const toggleSingleFilter = (filterType, value) => {
        setFilters((previous) => {
            const current = previous?.[filterType] || [];

            return {
                ...previous,
                [filterType]: current.includes(value) ? [] : [value],
            };
        });
    };

    const clearAll = () => {
        clearGlobalSearch();
        clearAdvancedSearch();

        setFilters(
            Object.fromEntries(
                Object.keys(filters || {}).map((key) => [key, []]),
            ),
        );
    };

    return (
        <div className="advanced-search-tool mb-3">
            <div className="advanced-search-toolbar">
                <InputGroup className="advanced-search-input">
                    <InputGroup.Text>
                        <FaSearch />
                    </InputGroup.Text>

                    <Form.Control
                        type="search"
                        value={inputValue}
                        onChange={(event) => setInputValue(event.target.value)}
                        placeholder={globalSearchPlaceholder}
                        autoComplete="off"
                        aria-label="Global search"
                    />

                    {inputValue && (
                        <Button
                            variant="outline-secondary"
                            className="advanced-search-clear"
                            onClick={clearGlobalSearch}
                            aria-label="Clear global search"
                        >
                            <FaTimes />
                        </Button>
                    )}
                </InputGroup>

                <div className="advanced-search-buttons">
                    {Object.keys(filterOptions).length > 0 && (
                        <Dropdown autoClose="outside">
                            <Dropdown.Toggle
                                variant="outline-secondary"
                                className={[
                                    'advanced-toolbar-button',
                                    hasFilters ? 'is-active' : '',
                                ].join(' ')}
                            >
                                <FaFilter className="me-2" />

                                <span>Filters</span>

                                {hasFilters && (
                                    <Badge
                                        bg="primary"
                                        pill
                                        className="ms-2"
                                    >
                                        {filterCount}
                                    </Badge>
                                )}
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                                align="end"
                                className="advanced-filter-menu p-2"
                            >
                                {Object.entries(filterOptions).map(
                                    ([filterType, options], index) => (
                                        <React.Fragment key={filterType}>
                                            <Dropdown.Header className="px-2 text-capitalize fw-semibold">
                                                {filterType}
                                            </Dropdown.Header>

                                            {options.map((value) => {
                                                const active = (
                                                    filters?.[filterType] || []
                                                ).includes(value);

                                                return (
                                                    <Dropdown.Item
                                                        key={value}
                                                        as="button"
                                                        active={active}
                                                        className="d-flex justify-content-between align-items-center rounded"
                                                        onClick={() =>
                                                            toggleSingleFilter(
                                                                filterType,
                                                                value,
                                                            )
                                                        }
                                                    >
                                                        <span className="text-capitalize">
                                                            {value}
                                                        </span>

                                                        {active && (
                                                            <span>✓</span>
                                                        )}
                                                    </Dropdown.Item>
                                                );
                                            })}

                                            {index <
                                                Object.keys(filterOptions).length - 1 && (
                                                    <Dropdown.Divider />
                                                )}
                                        </React.Fragment>
                                    ),
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}

                    <Button
                        variant="outline-secondary"
                        className={[
                            'advanced-toolbar-button',
                            advancedOpen ? 'is-open' : '',
                            hasAdvancedSearch ? 'is-active' : '',
                        ].join(' ')}
                        onClick={() =>
                            setAdvancedOpen((previous) => !previous)
                        }
                        aria-expanded={advancedOpen}
                    >
                        <FaSlidersH className="me-2" />

                        <span>Advanced search</span>

                        {hasAdvancedSearch && (
                            <Badge
                                bg="primary"
                                pill
                                className="ms-2"
                            >
                                {advancedSearchCount}
                            </Badge>
                        )}

                        {advancedOpen ? (
                            <FaChevronUp className="ms-2" />
                        ) : (
                            <FaChevronDown className="ms-2" />
                        )}
                    </Button>
                </div>
            </div>

            <Collapse in={advancedOpen}>
                <div>
                    <div className="advanced-search-panel">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <div className="fw-semibold">
                                    Advanced search
                                </div>

                                <div className="small text-muted">
                                    Search using specific model, brand or
                                    location fields.
                                </div>
                            </div>

                            {hasAdvancedSearch && (
                                <Badge bg="primary">
                                    {advancedSearchCount} active
                                </Badge>
                            )}
                        </div>

                        <Row className="g-3 align-items-end">
                            {exactSearchFields.map((field) => (
                                <Col
                                    xl={4}
                                    lg={4}
                                    md={6}
                                    xs={12}
                                    key={field.key}
                                >
                                    <Form.Group>
                                        <Form.Label className="small fw-semibold mb-1">
                                            {field.label}
                                        </Form.Label>

                                        <Form.Control
                                            value={
                                                exactDraft?.[field.key] || ''
                                            }
                                            onChange={(event) =>
                                                setExactDraft((previous) => ({
                                                    ...previous,
                                                    [field.key]:
                                                        event.target.value,
                                                }))
                                            }
                                            placeholder={
                                                field.placeholder ||
                                                field.label
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault();
                                                    applyAdvancedSearch();
                                                }
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            ))}

                            <Col xs={12}>
                                <div className="d-flex justify-content-end gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={clearAdvancedSearch}
                                        disabled={
                                            !hasAdvancedSearch &&
                                            !hasAdvancedDraft
                                        }
                                    >
                                        Clear
                                    </Button>

                                    <Button
                                        variant="primary"
                                        onClick={applyAdvancedSearch}
                                    >
                                        Apply search
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
            </Collapse>

            {(globalSearch || hasAdvancedSearch || hasFilters) && (
                <div className="advanced-search-active mt-2">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {globalSearch && (
                            <Badge
                                bg="light"
                                text="dark"
                                className="advanced-search-chip"
                            >
                                Search: {globalSearch}
                            </Badge>
                        )}

                        {Object.entries(exactSearch || {}).map(
                            ([key, value]) => {
                                if (!value) return null;

                                const label =
                                    exactSearchFields.find(
                                        (field) => field.key === key,
                                    )?.label || key;

                                return (
                                    <Badge
                                        key={key}
                                        bg="light"
                                        text="dark"
                                        className="advanced-search-chip"
                                    >
                                        {label}: {value}
                                    </Badge>
                                );
                            },
                        )}

                        {Object.entries(filters || {}).flatMap(
                            ([key, values]) => {
                                if (!Array.isArray(values)) return [];

                                return values.map((value) => (
                                    <Badge
                                        key={`${key}-${value}`}
                                        bg="light"
                                        text="dark"
                                        className="advanced-search-chip text-capitalize"
                                    >
                                        {key}: {value}
                                    </Badge>
                                ));
                            },
                        )}

                        <Button
                            variant="link"
                            size="sm"
                            className="text-danger text-decoration-none ms-auto"
                            onClick={clearAll}
                        >
                            Clear all
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

const SmartTopTool = ({
    advancedSearch = false,
    globalSearch = '',
    setGlobalSearch = () => { },
    globalSearchPlaceholder = 'Search...',
    exactSearch = {},
    setExactSearch = () => { },
    exactSearchFields = [],
    debounceMs = 400,

    searchType = [],
    searchConditions = [],
    setSearchConditions = () => { },

    filterOptions = {},
    filters = {},
    setFilters = () => { },

    dateOptions = [],

    sortOptions = [],
    sort = {},
    setSort = () => { },
}) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [draftDateConfigs, setDraftDateConfigs] = useState([
        {
            type: '',
            operator: 'before',
            values: [null],
        },
    ]);

    const operatorOptions = useMemo(
        () => ({
            before: {
                label: 'Before',
                inputs: 1,
            },
            after: {
                label: 'After',
                inputs: 1,
            },
            between: {
                label: 'Between',
                inputs: 2,
            },
            exact: {
                label: 'On',
                inputs: 1,
            },
        }),
        [],
    );

    const [currentDateFilter, setCurrentDateFilter] = useState({
        type: '',
        operator: 'before',
        values: [null, null],
    });

    const [searchInput, setSearchInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const searchRef = useRef(null);

    useEffect(() => {
        if (!isFilterOpen) {
            return;
        }

        const dateFilters = filters.dateFilters || [];

        setDraftDateConfigs(
            dateFilters.map((config) => ({
                ...config,
                values: config.values.map((date) => (
                    date ? new Date(date) : null
                )),
            })),
        );
    }, [
        filters.dateFilters,
        isFilterOpen,
    ]);

    useEffect(() => {
        const inputCount =
            operatorOptions[currentDateFilter.operator]?.inputs || 0;

        setCurrentDateFilter((previous) => {
            const nextValues = previous.values.slice(0, inputCount);

            while (nextValues.length < inputCount) {
                nextValues.push(null);
            }

            return {
                ...previous,
                values: nextValues,
            };
        });
    }, [
        currentDateFilter.operator,
        operatorOptions,
    ]);

    useEffect(() => {
        setSelectedIndex(-1);
    }, [showSuggestions]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!showSuggestions) {
                return;
            }

            if (event.key === 'ArrowDown') {
                setSelectedIndex((previous) => (
                    Math.min(previous + 1, searchType.length - 1)
                ));
            } else if (event.key === 'ArrowUp') {
                setSelectedIndex((previous) => (
                    Math.max(previous - 1, 0)
                ));
            } else if (
                event.key === 'Enter' &&
                selectedIndex >= 0
            ) {
                handleAddSearch(searchType[selectedIndex].key);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        searchType,
        selectedIndex,
        showSuggestions,
    ]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickOutside,
            );
        };
    }, []);

    const handleFilterToggle = (isOpen) => {
        setIsFilterOpen(isOpen);
    };

    const updateDraftConfig = (index, field, value) => {
        setDraftDateConfigs((previous) => (
            previous.map((config, configIndex) => (
                configIndex === index
                    ? {
                        ...config,
                        [field]: value,
                    }
                    : config
            ))
        ));
    };

    const updateDraftDate = (index, valueIndex, date) => {
        setDraftDateConfigs((previous) => {
            const currentConfig = previous[index];

            if (!currentConfig) {
                return previous;
            }

            const nextValues = [...currentConfig.values];
            nextValues[valueIndex] = date;

            return previous.map((config, configIndex) => (
                configIndex === index
                    ? {
                        ...config,
                        values: nextValues,
                    }
                    : config
            ));
        });
    };

    const isValidConfig = (config) => {
        const requiredInputs =
            operatorOptions[config.operator]?.inputs || 0;

        return (
            config.type &&
            config.values
                .slice(0, requiredInputs)
                .every((date) => (
                    date instanceof Date &&
                    !Number.isNaN(date.getTime())
                ))
        );
    };

    const applyDateFilters = () => {
        const validConfigs = draftDateConfigs.filter(isValidConfig);

        setFilters((previous) => ({
            ...previous,
            dateFilters: validConfigs.map((config) => ({
                ...config,
                values: config.values.map((date) => date.toISOString()),
            })),
        }));

        setIsFilterOpen(false);
    };

    const removeDateFilter = (indexToRemove) => {
        setFilters((previous) => ({
            ...previous,
            dateFilters: (previous.dateFilters || []).filter(
                (_, index) => index !== indexToRemove,
            ),
        }));
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
        setShowSuggestions(value.trim().length > 0);
    };

    const handleSearchFocus = (value) => {
        setShowSuggestions(value.trim().length > 0);
    };

    const handleAddSearch = (type) => {
        if (!searchInput.trim()) {
            return;
        }

        const newCondition = {
            type,
            value: searchInput.trim(),
        };

        setSearchConditions((previous) => [
            ...previous.filter((item) => item.type !== type),
            newCondition,
        ]);

        setSearchInput('');
        setShowSuggestions(false);
    };

    const handleMultiSelect = (filterType, value) => {
        setFilters((previous) => {
            const current = previous?.[filterType] || [];

            return {
                ...previous,
                [filterType]: current.includes(value)
                    ? current.filter((item) => item !== value)
                    : [...current, value],
            };
        });
    };

    const FilterSection = ({
        filterType,
        options,
        currentFilters,
        defaultExpanded = true,
    }) => {
        const [isExpanded, setIsExpanded] = useState(defaultExpanded);

        const selectedCount =
            currentFilters?.[filterType]?.length || 0;

        return (
            <>
                <Dropdown.Header
                    className="d-flex justify-content-between align-items-center filter-header"
                    onClick={() => setIsExpanded((expanded) => !expanded)}
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    <div className="d-flex align-items-center">
                        <span className="me-2">
                            {isExpanded ? '▼' : '►'}
                        </span>

                        <span className="text-capitalize">
                            {filterType}
                        </span>
                    </div>

                    <Badge bg="secondary">
                        {selectedCount}
                    </Badge>
                </Dropdown.Header>

                {isExpanded && (
                    <div className="filter-options">
                        {options.map((value) => {
                            const selected = (
                                currentFilters?.[filterType] || []
                            ).includes(value);

                            return (
                                <Dropdown.Item
                                    key={value}
                                    as="label"
                                    className={[
                                        'ps-5 rounded',
                                        selected ? 'active-filter' : '',
                                    ].join(' ')}
                                >
                                    <div className="d-flex justify-content-between align-items-center py-1">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selected}
                                                onChange={() => {
                                                    handleMultiSelect(
                                                        filterType,
                                                        value,
                                                    );
                                                }}
                                            />

                                            <span className="form-check-label">
                                                {value}
                                            </span>
                                        </div>

                                        {selected && (
                                            <span className="text-white">
                                                ✓
                                            </span>
                                        )}
                                    </div>
                                </Dropdown.Item>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    if (advancedSearch) {
        return (
            <AdvancedSearchTool
                globalSearch={globalSearch}
                setGlobalSearch={setGlobalSearch}
                globalSearchPlaceholder={globalSearchPlaceholder}
                exactSearch={exactSearch}
                setExactSearch={setExactSearch}
                exactSearchFields={exactSearchFields}
                filterOptions={filterOptions}
                filters={filters}
                setFilters={setFilters}
                debounceMs={debounceMs}
            />
        );
    }

    const hasNormalFilters = Object.values(filters || {}).some(
        (value) => Array.isArray(value) && value.length > 0,
    );

    return (
        <Row className="mb-3">
            <Col md={6}>
                <Form.Group
                    ref={searchRef}
                    className="position-relative"
                >
                    <InputGroup>
                        <InputGroup.Text>
                            <FaSearch />
                        </InputGroup.Text>

                        <Form.Control
                            type="search"
                            placeholder="Enter ..."
                            value={searchInput}
                            onChange={(event) => {
                                handleSearchChange(event.target.value);
                            }}
                            onFocus={(event) => {
                                handleSearchFocus(event.target.value);
                            }}
                            autoComplete="off"
                        />
                    </InputGroup>

                    {showSuggestions && (
                        <div
                            className="suggestions-menu position-absolute w-100 bg-white border mt-1 rounded shadow"
                            style={{
                                zIndex: 1000,
                            }}
                        >
                            {searchType.map(
                                ({ key, label }, index) => (
                                    <div
                                        key={key}
                                        className={[
                                            'suggestion-item p-3',
                                            index === selectedIndex
                                                ? 'selected'
                                                : '',
                                        ].join(' ')}
                                        onClick={() => handleAddSearch(key)}
                                        onMouseEnter={() => {
                                            setSelectedIndex(index);
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span className="text-muted">
                                                Search
                                            </span>

                                            <span className="ms-2 fw-medium">
                                                {label}
                                            </span>

                                            <span className="ms-2 text-truncate">
                                                "{searchInput}"
                                            </span>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    )}
                </Form.Group>
            </Col>

            <Col md={6}>
                <div className="d-flex gap-2">
                    <Dropdown
                        className="p-0"
                        autoClose="outside"
                        show={isFilterOpen}
                        onToggle={handleFilterToggle}
                    >
                        <Dropdown.Toggle variant="primary">
                            <FaFilter />
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                            className="p-2"
                            style={{
                                minWidth: 300,
                            }}
                        >
                            {Object.entries(filterOptions).map(
                                ([filterType, options], index) => (
                                    <React.Fragment key={filterType}>
                                        <FilterSection
                                            filterType={filterType}
                                            options={options}
                                            currentFilters={filters}
                                        />

                                        {index <
                                            Object.keys(filterOptions).length - 1 && (
                                                <Dropdown.Divider />
                                            )}
                                    </React.Fragment>
                                ),
                            )}

                            {Object.keys(filterOptions).length > 0 &&
                                dateOptions.length > 0 && (
                                    <Dropdown.Divider />
                                )}

                            {dateOptions.length > 0 && (
                                <div>
                                    <Dropdown.Header className="d-flex justify-content-between align-items-center">
                                        <span className="text-capitalize">
                                            Date Filters
                                        </span>

                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                                setDraftDateConfigs(
                                                    (previous) => [
                                                        ...previous,
                                                        {
                                                            type: '',
                                                            operator: 'before',
                                                            values: [null],
                                                        },
                                                    ],
                                                );
                                            }}
                                        >
                                            +
                                        </Button>
                                    </Dropdown.Header>

                                    <div className="px-3 pb-2">
                                        {draftDateConfigs.map(
                                            (config, index) => (
                                                <Row
                                                    key={index}
                                                    className="mb-3"
                                                >
                                                    <Col
                                                        sm={12}
                                                        className="d-flex gap-2 mb-2"
                                                    >
                                                        <Form.Select
                                                            value={config.type}
                                                            onChange={(event) => {
                                                                updateDraftConfig(
                                                                    index,
                                                                    'type',
                                                                    event.target.value,
                                                                );
                                                            }}
                                                            size="sm"
                                                        >
                                                            <option value="">
                                                                Select Type
                                                            </option>

                                                            {dateOptions.map(
                                                                (option) => (
                                                                    <option
                                                                        key={option}
                                                                        value={option}
                                                                    >
                                                                        {option.replace(
                                                                            /_/g,
                                                                            ' ',
                                                                        )}
                                                                    </option>
                                                                ),
                                                            )}
                                                        </Form.Select>

                                                        <Form.Select
                                                            value={config.operator}
                                                            onChange={(event) => {
                                                                updateDraftConfig(
                                                                    index,
                                                                    'operator',
                                                                    event.target.value,
                                                                );
                                                            }}
                                                            size="sm"
                                                        >
                                                            {Object.entries(
                                                                operatorOptions,
                                                            ).map(
                                                                ([
                                                                    key,
                                                                    { label },
                                                                ]) => (
                                                                    <option
                                                                        key={key}
                                                                        value={key}
                                                                    >
                                                                        {label}
                                                                    </option>
                                                                ),
                                                            )}
                                                        </Form.Select>
                                                    </Col>

                                                    <Col
                                                        sm={12}
                                                        className="d-flex gap-2 mb-2"
                                                    >
                                                        {Array.from({
                                                            length:
                                                                operatorOptions[
                                                                    config.operator
                                                                ]?.inputs || 0,
                                                        }).map((_, valueIndex) => (
                                                            <DatePicker
                                                                key={valueIndex}
                                                                selected={
                                                                    config.values[
                                                                        valueIndex
                                                                    ]
                                                                        ? new Date(
                                                                            config.values[
                                                                            valueIndex
                                                                            ],
                                                                        )
                                                                        : null
                                                                }
                                                                onChange={(date) => {
                                                                    updateDraftDate(
                                                                        index,
                                                                        valueIndex,
                                                                        date,
                                                                    );
                                                                }}
                                                                dateFormat="yyyy-MM-dd"
                                                                placeholderText={`Date ${valueIndex + 1
                                                                    }`}
                                                                className="form-control form-control-sm"
                                                            />
                                                        ))}

                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="d-flex align-items-center ms-auto"
                                                            onClick={() => {
                                                                setDraftDateConfigs(
                                                                    (previous) => (
                                                                        previous.filter(
                                                                            (
                                                                                _,
                                                                                configIndex,
                                                                            ) => (
                                                                                configIndex !==
                                                                                index
                                                                            ),
                                                                        )
                                                                    ),
                                                                );
                                                            }}
                                                        >
                                                            <FaTrash size={13} />
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ),
                                        )}

                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={applyDateFilters}
                                                disabled={
                                                    !draftDateConfigs.some(
                                                        isValidConfig,
                                                    )
                                                }
                                            >
                                                Apply
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>

                    {sortOptions?.length > 0 && (
                        <Dropdown
                            className="p-0"
                            autoClose="outside"
                        >
                            <Dropdown.Toggle variant="primary">
                                {sort.value ? (
                                    <span className="d-flex gap-1 align-items-center">
                                        {sort.value}

                                        {sort.order === 'asc' ? (
                                            <FaSortUp
                                                className="pt-1"
                                                size={20}
                                            />
                                        ) : (
                                            <FaSortDown
                                                className="pt-1"
                                                size={20}
                                            />
                                        )}
                                    </span>
                                ) : (
                                    <FaSort />
                                )}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                {sortOptions.map((field) => (
                                    <Dropdown.Item
                                        key={field}
                                        onClick={(event) => {
                                            event.stopPropagation();

                                            setSort((previous) => {
                                                if (
                                                    previous.value !== field
                                                ) {
                                                    return {
                                                        value: field,
                                                        order: 'desc',
                                                    };
                                                }

                                                if (
                                                    previous.order === 'desc'
                                                ) {
                                                    return {
                                                        value: field,
                                                        order: 'asc',
                                                    };
                                                }

                                                return {
                                                    value: null,
                                                    order: 'desc',
                                                };
                                            });
                                        }}
                                        className={[
                                            'd-flex justify-content-between',
                                            sort.value === field
                                                ? 'active-item'
                                                : '',
                                        ].join(' ')}
                                        style={{
                                            backgroundColor:
                                                sort.value === field
                                                    ? 'var(--bs-primary)'
                                                    : '',
                                            color:
                                                sort.value === field
                                                    ? 'white'
                                                    : '',
                                        }}
                                    >
                                        <span className="d-flex gap-1 align-items-center">
                                            {field}

                                            {sort.value === field && (
                                                sort.order === 'asc' ? (
                                                    <FaSortUp
                                                        className="pt-1"
                                                        size={20}
                                                    />
                                                ) : (
                                                    <FaSortDown
                                                        className="pt-1"
                                                        size={20}
                                                    />
                                                )
                                            )}
                                        </span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
            </Col>

            <Col md={12}>
                <Stack
                    direction="horizontal"
                    gap={2}
                    className="mt-2 flex-wrap"
                >
                    {searchConditions.map((condition, index) => (
                        <Button
                            key={`${condition.type}-${index}`}
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                                setSearchConditions((previous) => (
                                    previous.filter(
                                        (_, itemIndex) => itemIndex !== index,
                                    )
                                ));
                            }}
                        >
                            {condition.type}: {condition.value} ×
                        </Button>
                    ))}

                    <div className="d-flex flex-wrap gap-2">
                        {Object.entries(filters).map(
                            ([filterType, filterValues]) => {
                                if (
                                    !Array.isArray(filterValues) ||
                                    filterValues.length === 0
                                ) {
                                    return null;
                                }

                                if (filterType === 'dateFilters') {
                                    return filterValues.map(
                                        (dateFilter, index) => {
                                            const operatorLabel =
                                                operatorOptions[
                                                    dateFilter.operator
                                                ]?.label || 'Unknown';

                                            const formattedDates =
                                                dateFilter.values
                                                    .map((date) => (
                                                        date
                                                            ? new Date(
                                                                date,
                                                            ).toLocaleDateString(
                                                                'en-CA',
                                                            )
                                                            : '?'
                                                    ))
                                                    .join(' - ');

                                            return (
                                                <Button
                                                    key={`${dateFilter.type}-${index}`}
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => {
                                                        removeDateFilter(index);
                                                    }}
                                                >
                                                    <span className="me-1">
                                                        {dateFilter.type.replace(
                                                            /_/g,
                                                            ' ',
                                                        )}{' '}
                                                        {operatorLabel}:{' '}
                                                        {formattedDates}
                                                    </span>

                                                    <span className="text-danger">
                                                        ×
                                                    </span>
                                                </Button>
                                            );
                                        },
                                    );
                                }

                                return (
                                    <Button
                                        key={filterType}
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            setFilters((previous) => ({
                                                ...previous,
                                                [filterType]: [],
                                            }));
                                        }}
                                    >
                                        {filterType}: {filterValues.join(', ')}

                                        <span className="ms-1">
                                            ×
                                        </span>
                                    </Button>
                                );
                            },
                        )}
                    </div>

                    {sort.value && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                                setSort({
                                    value: null,
                                    order: 'desc',
                                });
                            }}
                        >
                            Sorted by: {sort.value}{' '}

                            {sort.order === 'asc' ? (
                                <FaSortUp
                                    className="pt-1"
                                    size={20}
                                />
                            ) : (
                                <FaSortDown
                                    className="pt-1"
                                    size={20}
                                />
                            )}
                        </Button>
                    )}

                    {(searchConditions.length > 0 ||
                        hasNormalFilters ||
                        sort.value) && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                className="ms-auto"
                                onClick={() => {
                                    setSearchConditions([]);

                                    setFilters(
                                        Object.fromEntries(
                                            Object.keys(filters).map(
                                                (key) => [key, []],
                                            ),
                                        ),
                                    );

                                    setSort({
                                        value: null,
                                        order: 'desc',
                                    });
                                }}
                            >
                                Clean All ×
                            </Button>
                        )}
                </Stack>
            </Col>
        </Row>
    );
};

export default SmartTopTool;